/**
 * First-party pageview beacon -> the site-beacon Supabase edge function ->
 * site_page_views, read by Command Center's reporting engine (site_traffic
 * subject). One POST per route view: page, referrer, UTM params, and an
 * anonymous per-browser-session key (sessionStorage, no cookies, no PII).
 *
 * Fire-and-forget by design: sendBeacon when available (survives unloads),
 * keepalive fetch otherwise, and every failure path is swallowed - analytics
 * must never break a page. PROD only, mirroring the Datadog RUM gate.
 *
 * Gated on FUNCTIONAL consent (FIV-499). Our own cookie notice files
 * "first-party usage measurement" under Functional, and this is exactly that,
 * so it must honour the same gate Google Analytics and Datadog RUM do: denied
 * in EEA/UK/CH until the visitor opts in, allowed by regional default
 * elsewhere. Checked per send rather than once at mount, so accepting the
 * banner starts counting from the next route view without a reload.
 */

import { hasFunctionalConsent } from "@/lib/consent/consent";

const SESSION_STORAGE_KEY = "fivestar-beacon-session";

function sessionKey(): string | null {
  try {
    let key = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!key) {
      key = crypto.randomUUID();
      sessionStorage.setItem(SESSION_STORAGE_KEY, key);
    }
    return key;
  } catch {
    return null; // storage blocked: still count the view, just sessionless
  }
}

export function sendPageView(pathname: string): void {
  if (!import.meta.env.PROD) return;
  if (!hasFunctionalConsent()) return; // also keeps sessionStorage untouched
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-beacon`;
    const params = new URLSearchParams(window.location.search);
    const payload = JSON.stringify({
      page: pathname,
      referrer: document.referrer || null,
      utmSource: params.get("utm_source"),
      utmMedium: params.get("utm_medium"),
      utmCampaign: params.get("utm_campaign"),
      sessionKey: sessionKey(),
    });
    if (navigator.sendBeacon?.(url, new Blob([payload], { type: "text/plain" }))) return;
    void fetch(url, { method: "POST", body: payload, keepalive: true }).catch(() => {});
  } catch {
    // never let analytics touch the page
  }
}
